<template>
    <section v-if="current" class="px-3 lg:px-28 py-16 lg:py-24">
        <div class="text-center mb-8">
            <h2 class="font-semibold">One request. Every {{ current.noun }}.</h2>
            <p class="subtitle mt-4 max-w-3xl mx-auto">
                Swap the <code class="text-[var(--secondary-blue)]">connection_id</code> and the same code reads
                {{ current.objects }} {{ source }}, in one normalized shape. Every call is live against the source API.
            </p>
        </div>

        <!-- WAI-ARIA tabs: arrow keys / Home / End move between tabs; only the selected tab is in the Tab order. -->
        <div v-if="samples.length > 1" class="flex flex-wrap justify-center gap-2 mb-6" role="tablist"
            aria-label="API category" @keydown="onTabKeydown($event, samples.map((c) => c.id), selectCategory)">
            <button v-for="cat in samples" :id="`first-call-tab-${cat.id}`" :key="cat.id" type="button" role="tab"
                :aria-selected="category === cat.id" :aria-controls="`first-call-panel-${cat.id}`"
                :tabindex="category === cat.id ? 0 : -1" class="px-4 py-2 rounded-full border text-sm font-semibold"
                :class="category === cat.id ? 'bg-[var(--primary-blue-cta)] border-[var(--primary-blue-cta)] text-[var(--white)]' : 'text-medium-gray hover:text-[var(--light-gray)]'"
                @click="category = cat.id">
                {{ cat.label }}
            </button>
        </div>

        <!-- Every category x language sample is server-rendered (v-show) so crawlers and LLMs see them all. -->
        <div v-for="cat in samples" v-show="category === cat.id" :id="`first-call-panel-${cat.id}`" :key="cat.id"
            :role="samples.length > 1 ? 'tabpanel' : undefined"
            :aria-labelledby="samples.length > 1 ? `first-call-tab-${cat.id}` : undefined"
            class="grid lg:grid-cols-2 gap-6 max-w-6xl mx-auto text-left">
            <div class="rounded-lg border bg-[var(--dark-gray)] overflow-hidden flex flex-col">
                <div class="flex items-center border-b">
                    <div role="tablist" :aria-label="`${cat.label} request language`" class="flex"
                        @keydown="onTabKeydown($event, LANGUAGES.map((l) => l.id), selectLanguage)">
                        <button v-for="lang in LANGUAGES" :id="`first-call-${cat.id}-${lang.id}-tab`" :key="lang.id"
                            type="button" role="tab" :aria-selected="language === lang.id"
                            :aria-controls="`first-call-${cat.id}-${lang.id}`" :tabindex="language === lang.id ? 0 : -1"
                            class="px-4 py-3 text-sm font-semibold border-b-2 -mb-px"
                            :class="language === lang.id ? 'border-[var(--primary-blue)] text-[var(--light-gray)]' : 'border-transparent text-medium-gray hover:text-[var(--light-gray)]'"
                            @click="language = lang.id">
                            {{ lang.label }}
                        </button>
                    </div>
                    <button type="button" class="ml-auto mr-3 text-sm text-medium-gray hover:text-[var(--light-gray)]"
                        :aria-label="`Copy ${current.label} ${LANGUAGES.find((l) => l.id === language)?.label} code`"
                        :title="copied ? 'Copied' : 'Copy code'" @click="copy">
                        <Icon :name="copied ? 'material-symbols:check-small-rounded' : 'material-symbols:content-copy-outline-rounded'"
                            class="text-lg" aria-hidden="true" />
                    </button>
                </div>
                <!-- Scrollable regions are focusable (tabindex=0) so keyboard users can scroll long lines. -->
                <pre v-for="lang in LANGUAGES" v-show="language === lang.id" :id="`first-call-${cat.id}-${lang.id}`"
                    :key="lang.id" role="tabpanel" :aria-labelledby="`first-call-${cat.id}-${lang.id}-tab`" tabindex="0"
                    class="flex-1 p-4 text-xs leading-5 font-mono text-[var(--primary-green)] overflow-x-auto">{{ cat.code[lang.id] }}</pre>
            </div>

            <div class="rounded-lg border bg-[var(--dark-gray)] overflow-hidden flex flex-col">
                <div :id="`first-call-${cat.id}-response`"
                    class="px-4 py-3 border-b text-sm font-semibold text-[var(--light-gray)] flex items-center gap-2">
                    Response
                    <span class="text-medium-gray font-normal">— same fields for every {{ cat.noun }}</span>
                </div>
                <pre :aria-labelledby="`first-call-${cat.id}-response`" tabindex="0"
                    class="flex-1 p-4 text-xs leading-5 font-mono text-[var(--light-gray)] overflow-x-auto">{{ cat.response }}</pre>
            </div>
        </div>
        <p class="sr-only" aria-live="polite">{{ copied ? 'Code copied to clipboard' : '' }}</p>

        <div class="flex flex-wrap justify-center gap-x-8 gap-y-3 mt-10 text-sm">
            <a v-for="link in LINKS" :key="link.href" :href="link.href" target="_blank" rel="noopener"
                class="flex items-center gap-2 text-[var(--light-gray)] hover:text-[var(--primary-blue)]">
                <Icon :name="link.icon" class="text-lg" />
                {{ link.title }}
            </a>
        </div>

        <div class="flex flex-col items-center gap-3 mt-10 text-center">
            <Button href="https://app.unified.to" title="Start free 30-day trial" :full-width-mobile="false" />
            <p class="text-sm text-medium-gray max-w-xl">
                No credit card required. Get your API key, connect real accounts, and make live calls like this one
                in minutes.
            </p>
        </div>
    </section>
</template>

<script lang="ts">
import { type PropType } from 'vue';
import { INTEGRATIONS } from '~~/services/integrations';
import { API_SAMPLES, HOMEPAGE_SAMPLE_IDS, type IApiSample } from '~~/data/api_samples';

type TLanguage = 'curl' | 'typescript' | 'python';

function buildCode(s: IApiSample): Record<TLanguage, string> {
    const params = [...(s.params || [])];
    const limit = s.limit === undefined ? { name: 'limit', ts: 'limit' } : s.limit;

    const curlData = [...params.map((p) => `  --data '${p.name}=${p.value}' \\`), ...(limit ? [`  --data '${limit.name}=1' \\`] : [])];
    const tsArgs = [`  connectionId: '{connection_id}',`, ...params.map((p) => `  ${p.ts}: '${p.value}',`), ...(limit ? [`  ${limit.ts}: 1,`] : [])];
    const pyArgs = [
        `        "connection_id": "{connection_id}",`,
        ...params.map((p) => `        "${p.name}": "${p.value}",`),
        ...(limit ? [`        "${limit.name}": 1,`] : []),
    ];

    return {
        curl: `curl --get \\
  'https://api.unified.to/${s.id}/{connection_id}/${s.path}' \\
${curlData.join('\n')}
  --header "authorization: bearer $UNIFIED_API_KEY"`,
        typescript: `// npm install @unified-api/typescript-sdk
import { UnifiedTo } from '@unified-api/typescript-sdk';

const unified = new UnifiedTo({
  security: { jwt: process.env.UNIFIED_API_KEY },
});

const ${s.variable} = await unified.${s.id}.${s.ts}({
${tsArgs.join('\n')}
});`,
        python: `# pip install Unified-python-sdk
import os
from unified_python_sdk import UnifiedTo
from unified_python_sdk.models import shared

with UnifiedTo(
    security=shared.Security(jwt=os.environ["UNIFIED_API_KEY"]),
) as unified:
    res = unified.${s.id}.${s.py}(request={
${pyArgs.join('\n')}
    })
    print(res.${s.pyResult})`,
    };
}

const LANGUAGES: { id: TLanguage; label: string }[] = [
    { id: 'curl', label: 'cURL' },
    { id: 'typescript', label: 'TypeScript' },
    { id: 'python', label: 'Python' },
];

const LINKS = [
    { title: 'API reference', href: 'https://docs.unified.to/intro', icon: 'material-symbols:menu-book-outline-rounded' },
    { title: 'OpenAPI spec', href: 'https://api.unified.to/swagger.json', icon: 'material-symbols:data-object-rounded' },
    { title: 'Postman collection', href: 'https://www.postman.com/unified-to/unified-api-workspace/overview', icon: 'simple-icons:postman' },
    { title: 'SDKs on GitHub', href: 'https://github.com/unified-to', icon: 'mdi:github' },
];

export default {
    props: {
        /** API categories to show, in tab order. One category hides the tab row. Unknown ids are skipped. */
        categories: {
            type: Array as PropType<string[]>,
            default: () => HOMEPAGE_SAMPLE_IDS,
        },
    },
    data() {
        const samples = this.categories
            .map((id) => API_SAMPLES[id])
            .filter((s): s is IApiSample => !!s)
            .map((s) => {
                const integrations = INTEGRATIONS.filter((i) => (i.categories as string[]).includes(s.integrationCategory || s.id));
                return {
                    ...s,
                    code: buildCode(s),
                    count: integrations.length,
                    // Curated examples where set, else the three most popular integrations in the data.
                    examples: s.examples || [...integrations].sort((a, b) => (b.popularity || 0) - (a.popularity || 0)).slice(0, 3).map((i) => i.name),
                };
            });

        return {
            samples,
            LANGUAGES,
            LINKS,
            category: samples[0]?.id || '',
            language: 'curl' as TLanguage,
            copied: false,
        };
    },
    computed: {
        /** "from HubSpot, Salesforce, Pipedrive or any of 224 CRM integrations", tolerating sparse data. */
        source(): string {
            const c = this.current;
            if (!c) return '';
            const all = c.count ? `any of ${c.count} ${c.countLabel} integrations` : `every ${c.countLabel} integration`;
            return c.examples.length ? `from ${c.examples.join(', ')} or ${all}` : `from ${all}`;
        },
        current() {
            return this.samples.find((c) => c.id === this.category) ?? this.samples[0];
        },
    },
    methods: {
        selectCategory(id: string) {
            this.category = id;
        },
        selectLanguage(id: string) {
            const lang = LANGUAGES.find((l) => l.id === id);
            if (lang) this.language = lang.id;
        },
        /** Arrow keys / Home / End within a tablist: select the target tab and move focus to it. */
        onTabKeydown(event: KeyboardEvent, ids: string[], select: (id: string) => void) {
            const tabs = [...((event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('[role="tab"]'))];
            const index = tabs.indexOf(event.target as HTMLElement);
            if (index < 0) return;
            const next =
                event.key === 'ArrowRight' ? (index + 1) % tabs.length
                : event.key === 'ArrowLeft' ? (index - 1 + tabs.length) % tabs.length
                : event.key === 'Home' ? 0
                : event.key === 'End' ? tabs.length - 1
                : -1;
            if (next < 0) return;
            event.preventDefault();
            select(ids[next]!);
            tabs[next]!.focus();
        },
        copy() {
            const code = this.current?.code[this.language];
            if (!code || !navigator?.clipboard?.writeText) return;
            navigator.clipboard.writeText(code).then(() => {
                this.copied = true;
                setTimeout(() => (this.copied = false), 2000);
            });
        },
    },
};
</script>
